if (!customElements.get('bm-landing-ring-builder')) {
  class BmLandingRingBuilder extends HTMLElement {
    connectedCallback() {
      this.stepEls = Array.from(this.querySelectorAll('[data-bm-lrb-step]'));
      this.panelEls = Array.from(this.querySelectorAll('[data-bm-lrb-panel]'));
      this.stepsScroll = this.querySelector('[data-bm-lrb-steps-scroll]');

      if (this.stepEls.length === 0) return;

      this.activeIndex = 0;
      this.autoplayDelay = parseInt(this.dataset.autoplay, 10) || 5000;
      this.autoplayEnabled = this.dataset.autoplayEnabled === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.mobileStepsScroll = window.matchMedia('(max-width: 989px)');
      this.progressFillLine = this.querySelector('[data-bm-lrb-progress-fill]');
      this.progressTrackHeight = 472.5;

      this.stepEls.forEach((stepEl, index) => {
        const trigger = stepEl.querySelector('[data-bm-lrb-step-trigger]');
        if (!trigger) return;
        trigger.addEventListener('click', () => this.goTo(index, { user: true }));
        trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event, index));
      });

      this.onMouseEnter = () => this.pauseAutoplay();
      this.onMouseLeave = () => this.startAutoplay();
      this.onFocusIn = () => this.pauseAutoplay();
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.startAutoplay();
        }
      };

      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);

      this.goTo(0, { user: false });
      this.startAutoplay();

      this.initMobileCarousel();
    }

    disconnectedCallback() {
      this.pauseAutoplay();
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);

      if (this.mCardsScroll && this.onMobileScroll) {
        this.mCardsScroll.removeEventListener('scroll', this.onMobileScroll);
      }
      if (this.onMobileResize) {
        window.removeEventListener('resize', this.onMobileResize);
      }
    }

    // Mobile-only carousel (≤989px). Native horizontal scroll drives which card is
    // active (body reveal) and the progress-rail fill. Desktop step logic is separate.
    initMobileCarousel() {
      this.mCards = Array.from(this.querySelectorAll('[data-bm-lrb-mcard]'));
      this.mCardsScroll = this.querySelector('[data-bm-lrb-mcards]');
      this.mRailFill = this.querySelector('[data-bm-lrb-mrail-fill]');

      if (!this.mCardsScroll || this.mCards.length === 0) return;

      let ticking = false;
      const sync = () => {
        ticking = false;
        this.syncMobileCarousel();
      };
      this.onMobileScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(sync);
      };
      this.onMobileResize = this.onMobileScroll;

      this.mCardsScroll.addEventListener('scroll', this.onMobileScroll, { passive: true });
      window.addEventListener('resize', this.onMobileResize, { passive: true });
      this.syncMobileCarousel();
    }

    syncMobileCarousel() {
      const scroller = this.mCardsScroll;
      if (!scroller) return;

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const fillRatio = scroller.scrollWidth > 0 ? (scroller.scrollLeft + scroller.clientWidth) / scroller.scrollWidth : 1;
      if (this.mRailFill) {
        this.mRailFill.style.width = `${Math.min(100, Math.max(8, fillRatio * 100))}%`;
      }

      // Active card = the one snapped nearest the scroller's left edge.
      let stepWidth = 1;
      if (this.mCards.length > 1) {
        stepWidth = this.mCards[1].getBoundingClientRect().left - this.mCards[0].getBoundingClientRect().left;
      }
      let index = stepWidth > 0 ? Math.round(scroller.scrollLeft / stepWidth) : 0;
      if (maxScroll > 0 && scroller.scrollLeft >= maxScroll - 1) {
        index = this.mCards.length - 1;
      }
      index = Math.max(0, Math.min(this.mCards.length - 1, index));

      this.mCards.forEach((card, i) => card.classList.toggle('is-active', i === index));
    }

    onTriggerKeydown(event, index) {
      const { key } = event;
      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.goTo(index, { user: true });
        return;
      }

      if (key === 'ArrowDown' || key === 'ArrowRight') {
        event.preventDefault();
        this.goTo((index + 1) % this.stepEls.length, { user: true });
        return;
      }

      if (key === 'ArrowUp' || key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo((index - 1 + this.stepEls.length) % this.stepEls.length, { user: true });
      }
    }

    getProgressRatio(index) {
      const count = this.stepEls.length;
      if (count <= 1) return 1;
      const clamped = Math.max(0, Math.min(index, count - 1));
      return (clamped + 1) / count;
    }

    applyProgressFill(index) {
      const ratio = this.getProgressRatio(index);
      this.style.setProperty('--bm-lrb-progress', String(ratio));

      if (this.progressFillLine) {
        this.progressFillLine.setAttribute('y2', String(ratio * this.progressTrackHeight));
      }
    }

    goTo(index, { user = false } = {}) {
      const count = this.stepEls.length;
      if (count === 0) return;

      const nextIndex = ((index % count) + count) % count;
      this.activeIndex = nextIndex;

      this.stepEls.forEach((stepEl, i) => {
        const isActive = i === nextIndex;
        const trigger = stepEl.querySelector('[data-bm-lrb-step-trigger]');
        const panel = stepEl.querySelector('[data-bm-lrb-step-panel]');

        stepEl.classList.toggle('is-active', isActive);
        if (trigger) {
          trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
          trigger.setAttribute('tabindex', isActive ? '0' : '-1');
        }
        if (panel) {
          panel.hidden = !isActive;
        }
      });

      this.panelEls.forEach((panelEl, i) => {
        const isActive = i === nextIndex;
        panelEl.classList.toggle('is-active', isActive);
        panelEl.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.applyProgressFill(nextIndex);

      const activeStep = this.stepEls[nextIndex];
      if (activeStep && this.stepsScroll && this.mobileStepsScroll.matches) {
        activeStep.scrollIntoView({ block: 'nearest', behavior: this.reducedMotion ? 'auto' : 'smooth' });
      }

      if (user) {
        this.restartAutoplay();
      }
    }

    nextStep() {
      this.goTo(this.activeIndex + 1, { user: false });
    }

    startAutoplay() {
      if (!this.autoplayEnabled || this.reducedMotion) return;
      this.pauseAutoplay();
      this.autoplayTimer = window.setInterval(() => this.nextStep(), this.autoplayDelay);
    }

    pauseAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      this.pauseAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('bm-landing-ring-builder', BmLandingRingBuilder);
}
